import * as Sentry from '@sentry/react'
import { AxiosError } from 'axios'
import Mixpanel from 'mixpanel-browser'

import { IResponse, OldIResponse } from '@/types/common'

type EventProperties = {
  [key: string]: string | number | boolean | null | number[] | object
}

type Domain =
  | 'PAGEVIEW'
  | 'ONBOARDING'
  | 'AUTH'
  | 'ACCOUNT'
  | 'RESET-PASSWORD'
  | 'UPDATE'
  | 'TAXES'

export const sendAnalyticsEvent = (
  domain: Domain,
  event: string,
  properties?: EventProperties
) => {
  const eventName = `${domain}|${event}`
  if (import.meta.env.VITE_MIXPANEL_ACTIVATED == 'true') {
    Mixpanel.track(eventName, properties)
  } else if (import.meta.env.VITE_MIXPANEL_DEBUGGING == 'true') {
    // eslint-disable-next-line no-console
    console.log('Tracking event: ', eventName, properties)
  }
}

export const trackOnboardingStart = () => {
  if (import.meta.env.VITE_MIXPANEL_ACTIVATED === 'true') {
    Mixpanel.track('onboarding:start')
  }
}

export const identifyUser = (id: string) => {
  if (import.meta.env.VITE_MIXPANEL_ACTIVATED == 'true') {
    Mixpanel.alias(id)
    Mixpanel.identify(id)
  }
  if (import.meta.env.VITE_SENTRY_ACTIVE === 'true') {
    Sentry.setUser({ id })
  }
}

export const clearMixpanelUser = () => {
  if (import.meta.env.VITE_MIXPANEL_ACTIVATED == 'true') {
    Mixpanel.reset()
  }
}

type UserProperties = {
  $email?: string
  $phone?: string
  $name?: string
}

export const setUserProperties = (properties: UserProperties) => {
  if (import.meta.env.VITE_MIXPANEL_ACTIVATED == 'true') {
    Mixpanel.people.set(properties)
  }
}

export const getEventPropertiesFromHttpError = (
  error: AxiosError<OldIResponse> | AxiosError<IResponse>
): EventProperties => {
  const baseProperties = {
    status: error.response?.status || error?.status || null,
    errorMessage: error?.message || 'Unknown error',
    errorName: error?.name || 'Unknown',
    path: error?.config?.url || null,
  }

  if (!error?.response?.data) {
    return baseProperties
  }

  if ('meta' in error.response.data) {
    return {
      ...baseProperties,
      code: error.response.data.meta?.code || null,
      responseDataMessage: error.response.data.meta?.message || null,
      errorDetail: error.response.data.meta?.errorDetail || null,
    }
  }

  return {
    ...baseProperties,
    path: error.response.data.path || baseProperties.path,
    status: error.response.data.status || baseProperties.status,
    error: error.response.data.error || null,
    errorCode: error.response.data.error || null,
    responseDataMessage: error.response.data.message || null,
  }
}

export const getEventPropertiesFromError = (error: unknown) => {
  return {
    errorMessage: error instanceof Error ? error.message : 'Unknown error',
    errorName: error instanceof Error ? error.name : 'Unknown',
  }
}
